/**
 * Base Map Form Validation
 * 
 * Validation utilities for base map form data.
 * Requirements: 29.6, 29.7, 37.7
 */

import type { BaseMapFormData } from '../types/baseMaps';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validate base map form data
 */
export function validateBaseMapForm(data: BaseMapFormData): ValidationResult {
  const errors: ValidationError[] = [];

  // Required fields
  if (!data.name || data.name.trim() === '') {
    errors.push({ field: 'name', message: 'Name is required' });
  }

  if (!data.thumbnailPath || data.thumbnailPath.trim() === '') {
    errors.push({ field: 'thumbnailPath', message: 'Thumbnail path is required' });
  }

  // Type-specific validation
  if (data.type === 'single-tile') {
    if (!data.overviewUrl || data.overviewUrl.trim() === '') {
      errors.push({ field: 'overviewUrl', message: 'Overview URL is required for single-tile maps' });
    }
    if (!data.geoTiffUrl || data.geoTiffUrl.trim() === '') {
      errors.push({ field: 'geoTiffUrl', message: 'GeoTIFF URL is required for single-tile maps' });
    }
  } else if (data.type === 'multi-tile') {
    if (!data.tileIndexPath || data.tileIndexPath.trim() === '') {
      errors.push({ field: 'tileIndexPath', message: 'Tile index path is required for multi-tile maps' });
    }
  }

  // Geographic bounds validation
  if (data.bounds.north < -90 || data.bounds.north > 90) {
    errors.push({ field: 'bounds.north', message: 'North latitude must be between -90 and 90' });
  }
  if (data.bounds.south < -90 || data.bounds.south > 90) {
    errors.push({ field: 'bounds.south', message: 'South latitude must be between -90 and 90' });
  }
  if (data.bounds.north <= data.bounds.south) {
    errors.push({ field: 'bounds.north', message: 'North latitude must be greater than south latitude' });
  }
  if (data.bounds.east < -180 || data.bounds.east > 180) {
    errors.push({ field: 'bounds.east', message: 'East longitude must be between -180 and 180' });
  }
  if (data.bounds.west < -180 || data.bounds.west > 180) {
    errors.push({ field: 'bounds.west', message: 'West longitude must be between -180 and 180' });
  }

  // Projection parameters validation
  if (!data.projection.type) {
    errors.push({ field: 'projection.type', message: 'Projection type is required' });
  }

  if (data.projection.type === 'lcc') {
    // Lambert Conformal Conic specific validation
    if (data.projection.centralMeridian === undefined) {
      errors.push({ field: 'projection.centralMeridian', message: 'Central meridian is required for Lambert Conformal Conic' });
    } else if (data.projection.centralMeridian < -180 || data.projection.centralMeridian > 180) {
      errors.push({ field: 'projection.centralMeridian', message: 'Central meridian must be between -180 and 180' });
    }

    if (data.projection.originLatitude === undefined) {
      errors.push({ field: 'projection.originLatitude', message: 'Origin latitude is required for Lambert Conformal Conic' });
    } else if (data.projection.originLatitude < -90 || data.projection.originLatitude > 90) {
      errors.push({ field: 'projection.originLatitude', message: 'Origin latitude must be between -90 and 90' });
    }

    if (data.projection.standardParallel1 === undefined) {
      errors.push({ field: 'projection.standardParallel1', message: 'Standard parallel 1 is required for Lambert Conformal Conic' });
    } else if (data.projection.standardParallel1 < -90 || data.projection.standardParallel1 > 90) {
      errors.push({ field: 'projection.standardParallel1', message: 'Standard parallel 1 must be between -90 and 90' });
    }

    if (data.projection.standardParallel2 === undefined) {
      errors.push({ field: 'projection.standardParallel2', message: 'Standard parallel 2 is required for Lambert Conformal Conic' });
    } else if (data.projection.standardParallel2 < -90 || data.projection.standardParallel2 > 90) {
      errors.push({ field: 'projection.standardParallel2', message: 'Standard parallel 2 must be between -90 and 90' });
    }

    // Validate standard parallel ordering
    if (
      data.projection.standardParallel1 !== undefined &&
      data.projection.standardParallel2 !== undefined &&
      data.projection.standardParallel1 >= data.projection.standardParallel2
    ) {
      errors.push({
        field: 'projection.standardParallel1',
        message: 'Standard parallel 1 must be less than standard parallel 2'
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get error message for a specific field
 */
export function getFieldError(errors: ValidationError[], field: string): string | undefined {
  const error = errors.find((e) => e.field === field);
  return error?.message;
}

/**
 * Check if a field has an error
 */
export function hasFieldError(errors: ValidationError[], field: string): boolean {
  return errors.some((e) => e.field === field);
}
